import {
  AssignmentOutlined,
  ApprovalOutlined,
  FactCheckOutlined,
  HomeOutlined,
  Inventory2Outlined,
  InventoryOutlined,
  LocalShippingOutlined,
  LockOutlined,
  MenuOpenOutlined,
  MoveToInboxOutlined,
  PrecisionManufacturingOutlined,
  RuleOutlined,
  ShoppingBasketOutlined,
  ShoppingCartCheckoutOutlined
} from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { APP_VERSION } from "./appVersion";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export type NavigationView =
  "home" | "suppliers" | "inbound" | "stock" | "allocations" |
  "preparations" | "warehouse" | "products" | "packaging" | "putaway" |
  "quality" | "derogations" | "repackaging" | "configuration" | "process-points" |
  "clients" | "deliveries" | "outbound-orders" | "documents" |
  "handling-equipment" | "handling-equipment-masterdata" |
  "putaway-work-queues" | "inventory-counts" |
  "access-management" | "flow-diagnostics" | "workflow-diagnostics" |
  "terminal-diagnostics" | "translations";

interface NavigationItem {
  label: string;
  view: NavigationView;
  permission?: string;
  permissions?: string[];
  icon: ReactNode;
}

function navigationGroups(t: (key: string) => string): Array<{ group: string; items: NavigationItem[] }> { return [
  {
    group: "",
    items: [
      { label: t("transverse.navigation.home"), view: "home", icon: <HomeOutlined /> }
    ]
  },
  {
    group: t("transverse.navigation.requests"),
    items: [
      { label: t("transverse.navigation.outboundOrders"), view: "outbound-orders", permission: "outbound_order:read", icon: <ShoppingCartCheckoutOutlined /> }
    ]
  },
  {
    group: t("transverse.navigation.operations"),
    items: [
      { label: t("transverse.navigation.inbound"), view: "inbound", permission: "asn:read", icon: <MoveToInboxOutlined /> },
      { label: t("transverse.navigation.putaway"), view: "putaway", permission: "putaway_task:read", icon: <RuleOutlined /> },
      { label: t("transverse.navigation.quality"), view: "quality", permission: "quality_inspection:read", icon: <FactCheckOutlined /> },
      { label: t("transverse.navigation.derogations"), view: "derogations", permission: "derogation:read", icon: <ApprovalOutlined /> },
      { label: t("transverse.navigation.repackaging"), view: "repackaging", permission: "repackaging_task:read", icon: <Inventory2Outlined /> },
      { label: t("transverse.navigation.allocations"), view: "allocations", permission: "allocation:read", icon: <LockOutlined /> },
      { label: t("transverse.navigation.preparation"), view: "preparations", permission: "picking:read", icon: <ShoppingBasketOutlined /> },
      { label: t("transverse.navigation.delivery"), view: "deliveries", permission: "delivery:read", icon: <LocalShippingOutlined /> },
      { label: t("transverse.navigation.equipment"), view: "handling-equipment",
        permissions: ["delivery:read", "picking:read", "putaway_task:read"],
        icon: <PrecisionManufacturingOutlined /> }
    ]
  },
  {
    group: t("transverse.navigation.stockGroup"),
    items: [
      { label: t("transverse.navigation.stock"), view: "stock", permission: "inventory:read", icon: <InventoryOutlined /> },
      { label: t("transverse.navigation.inventories"), view: "inventory-counts", permission: "inventory_count:read", icon: <FactCheckOutlined /> }
    ]
  }
]; }

interface NavigationContentProps {
  permissions: string[];
  currentView: NavigationView;
  expanded: boolean;
  mobile?: boolean;
  onToggle?: () => void;
  onNavigate: (view: NavigationView) => void;
}

function NavigationContent({
  permissions, currentView, expanded, mobile = false, onToggle, onNavigate
}: NavigationContentProps) {
  const { t } = useTranslation();
  const groups = navigationGroups(t);
  const visibleGroups = groups.map((group) => ({
    ...group,
    items: group.items.filter((item) => (!item.permission
      || permissions.includes(item.permission))
      && (!item.permissions
        || item.permissions.some((permission) => permissions.includes(permission))))
  })).filter((group) => group.items.length);

  return (
    <Box component="nav" aria-label={t("transverse.navigation.main")} sx={{
      width: mobile ? 280 : expanded ? 216 : 64,
      height: "100%",
      bgcolor: "primary.main",
      color: "primary.contrastText",
      transition: "width 180ms ease",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column"
    }}>
      <Box sx={{
        display: "flex", alignItems: "center",
        justifyContent: expanded ? "space-between" : "center",
        minHeight: 52, px: expanded ? 1.5 : .75,
        borderBottom: 1, borderColor: "rgba(255,255,255,.18)"
      }}>
        {expanded && (
          <Typography variant="subtitle2" fontWeight={800}>
            {t("transverse.navigation.title")}
          </Typography>
        )}
        {!mobile && (
          <Tooltip title={expanded ? t("transverse.navigation.collapse") : t("transverse.navigation.expand")}>
            <IconButton aria-label={expanded ? t("transverse.navigation.collapse") : t("transverse.navigation.expand")}
              onClick={onToggle}
              sx={{
                color: "inherit",
                transform: expanded ? "rotate(0deg)" : "rotate(180deg)",
                transition: "transform 180ms ease"
              }}>
              <MenuOpenOutlined />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Stack divider={<Divider flexItem sx={{ borderColor: "rgba(255,255,255,.18)" }} />}
        sx={{ minHeight: 0, flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        {visibleGroups.map((group) => (
          <Stack key={group.group} spacing={.35} sx={{ p: .65 }}>
            {expanded && group.group && (
              <Typography variant="overline" sx={{
                px: .75, lineHeight: 1.8, fontWeight: 800,
                color: "rgba(255,255,255,.68)"
              }}>
                {group.group}
              </Typography>
            )}
            {group.items.map((item) => {
              const selected = currentView === item.view;
              return (
                <Tooltip key={item.label} title={expanded ? "" : item.label}
                  placement="right">
                  <Button data-testid={`navigation-${item.view}`}
                    aria-label={item.label}
                    aria-current={selected ? "page" : undefined}
                    onClick={() => onNavigate(item.view)}
                    startIcon={item.icon}
                    sx={{
                      minWidth: 0, width: "100%", height: 42,
                      px: expanded ? 1.1 : 0,
                      justifyContent: expanded ? "flex-start" : "center",
                      textTransform: "none", fontWeight: 700,
                      whiteSpace: "nowrap", borderRadius: 1, color: "inherit",
                      bgcolor: selected ? "rgba(255,255,255,.18)" : "transparent",
                      "&:hover": { bgcolor: "rgba(255,255,255,.12)" },
                      "& .MuiButton-startIcon": { m: expanded ? undefined : 0 }
                    }}>
                    <Box component="span" sx={{
                      display: expanded ? "inline" : "none",
                      overflow: "hidden", textOverflow: "ellipsis"
                    }}>
                      {item.label}
                    </Box>
                  </Button>
                </Tooltip>
              );
            })}
          </Stack>
        ))}
      </Stack>
      {expanded && (
        <Typography variant="caption" sx={{
          flexShrink: 0,
          py: 1,
          px: 1.5,
          textAlign: "center",
          color: "rgba(255,255,255,.62)",
          borderTop: 1,
          borderColor: "rgba(255,255,255,.18)",
          fontWeight: 700,
          letterSpacing: ".04em"
        }}>
          Version {APP_VERSION}
        </Typography>
      )}
    </Box>
  );
}

interface AppNavigationProps {
  permissions: string[];
  currentView: NavigationView;
  expanded: boolean;
  mobileOpen: boolean;
  onToggle: () => void;
  onMobileClose: () => void;
  onNavigate: (view: NavigationView) => void;
}

export function AppNavigation(props: AppNavigationProps) {
  return (
    <>
      <Box sx={{
        display: { xs: "none", md: "block" },
        flexShrink: 0, minHeight: 0, height: "100%"
      }}>
        <NavigationContent permissions={props.permissions}
          currentView={props.currentView} expanded={props.expanded}
          onToggle={props.onToggle} onNavigate={props.onNavigate} />
      </Box>
      <Drawer open={props.mobileOpen} onClose={props.onMobileClose}
        variant="temporary"
        ModalProps={{ keepMounted: true }}
        slotProps={{ paper: { sx: { width: 280, border: 0 } } }}>
        <NavigationContent permissions={props.permissions}
          currentView={props.currentView} expanded mobile
          onNavigate={(view) => {
            props.onNavigate(view);
            props.onMobileClose();
          }} />
      </Drawer>
    </>
  );
}
